// "Edit aircraft" modal of a ShareJet booking: operator, then an aircraft free on the booking's date.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App as AntdApp, Input, Modal, Select, Space, Spin, Typography } from 'antd';

import { assignableAircraft, listOperators } from '../../api/endpoints';
import { CATEGORY_LABELS } from '../../api/types';
import type { AdminBookingDetail } from '../../api/types';
import { extractError, type SubmitFn } from './bookingUpdate';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';

const { Text } = Typography;

export function AircraftModal({
  open,
  onClose,
  booking,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  booking: AdminBookingDetail;
  onSubmit: SubmitFn;
  submitting: boolean;
}) {
  const { message } = AntdApp.useApp();
  const [operatorId, setOperatorId] = useState<string>(booking.aircraft.operator.id);
  const [aircraftId, setAircraftId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setOperatorId(booking.aircraft.operator.id);
      setAircraftId(undefined);
    }
  }, [open, booking]);

  const operatorsQ = useQuery({
    queryKey: ['operators', 'all'],
    queryFn: () => listOperators({ pageSize: 200, status: 'active' }),
    enabled: open,
  });
  // SHAR-414 — only jets that are FREE on this booking's date (and can seat the
  // booked seats) are offered; re-fires per operator.
  const aircraftQ = useQuery({
    queryKey: ['assignable-aircraft', booking.id, operatorId],
    queryFn: () => assignableAircraft(booking.id, operatorId),
    enabled: open && !!operatorId,
  });

  const selected = aircraftQ.data?.find((a) => a.id === aircraftId);

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const handleOk = submitGuard.wrap(async () => {
    if (!aircraftId) {
      message.error('Select an aircraft model');
      return;
    }
    try {
      await onSubmit({ aircraftId });
      message.success('Aircraft updated');
      onClose();
    } catch (e) {
      message.error(extractError(e));
    }
  });

  return (
    <Modal
      title="Edit aircraft"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={submitting || submitGuard.busy}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Only aircraft available on this booking's date are listed. Reassigning does not change the price or MGAV — they stay as booked."
      />
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Operator
          </Text>
          <Select
            value={operatorId}
            onChange={(v) => {
              setOperatorId(v);
              setAircraftId(undefined);
            }}
            loading={operatorsQ.isLoading}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            options={(operatorsQ.data?.items ?? []).map((o) => ({ value: o.id, label: o.name }))}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Aircraft model{' '}
            <Text type="secondary" style={{ fontSize: 12 }}>
              (current: {booking.aircraft.modelName}
              {booking.aircraft.tailNumber ? ` · ${booking.aircraft.tailNumber}` : ''})
            </Text>
          </Text>
          <Select
            value={aircraftId}
            onChange={setAircraftId}
            loading={aircraftQ.isLoading}
            placeholder="Select aircraft"
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            notFoundContent={aircraftQ.isLoading ? <Spin size="small" /> : 'No available aircraft'}
            options={(aircraftQ.data ?? []).map((a) => ({
              value: a.id,
              label: `${a.modelName} · ${a.tailNumber}`,
            }))}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Category
          </Text>
          <Input
            readOnly
            placeholder="Derived from selected aircraft"
            value={
              selected
                ? (CATEGORY_LABELS[selected.category as keyof typeof CATEGORY_LABELS] ??
                    selected.category)
                : ''
            }
          />
        </div>
      </Space>
    </Modal>
  );
}
